import { useCallback } from 'react'

import { invoiceDetailsSection } from '@services/sections/lib/billing'
import { useSegment, SegmentEvents } from '@services/segment'
import { BILLING_INVOICES_POLL_INTERVAL } from '@shared/apollo/pollInterval'
import { ComplexTable, TableCard } from '@shared/components/tables'
import { useInvoicesLazyQuery } from '@shared/graphql'
import { default as useIsMounted } from 'ismounted'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'

import { useInvoiceTableColumn } from './InvoicesTable.Column'

import type {
  Row,
  OnFetchDataProps,
} from '@circlefin/components/lib/AdvancedTable'
import type { Invoice } from '@shared/graphql'

const { Header, Title, Body } = TableCard

export interface InvoicesProps {
  /**
   * Hide header?
   */
  hideHeader?: boolean
  /**
   * Controls the amount of lines in the table for each paginated page.
   * Defaults to 10.
   */
  pageSize?: number
  /**
   * Whether we want to enable pagination.
   * Defaults to true.
   */
  enablePagination?: boolean
}

export const InvoicesTable: React.FC<InvoicesProps> = ({
  hideHeader = false,
  pageSize = 10,
  enablePagination = true,
}) => {
  const router = useRouter()
  const { t } = useTranslation('settings')
  const columns = useInvoiceTableColumn()
  const [fetchBillingList, { called, loading, data, error, refetch }] =
    useInvoicesLazyQuery({
      variables: { page: { pageSize } },
      fetchPolicy: 'cache-and-network',
      pollInterval: BILLING_INVOICES_POLL_INTERVAL, // 1 min interval
    })
  const { track } = useSegment()
  const isMounted = useIsMounted()

  const handleFetchData = useCallback(
    ({ currentPageLink }: OnFetchDataProps<Invoice>) => {
      if (isMounted.current) {
        void fetchBillingList({
          ...(currentPageLink && {
            variables: {
              page: {
                pageSize: currentPageLink.pageSize,
                to: currentPageLink.to,
                from: currentPageLink.from,
                pageAfter: currentPageLink.pageAfter,
                pageBefore: currentPageLink.pageBefore,
              },
            },
          }),
        })
      }
    },
    [fetchBillingList, isMounted],
  )

  const onRowClick = useCallback(
    ({ original: invoice }: Row<Invoice>) => {
      track(SegmentEvents.InvoiceDetailsClicked)
      void router.push({
        pathname: invoiceDetailsSection.route,
        query: {
          id: invoice.id,
        },
      })
    },
    [router, track],
  )

  return (
    <TableCard>
      <Header hidden={hideHeader}>
        <Title>{t`billing.title`}</Title>
      </Header>
      <Body>
        <ComplexTable<Invoice>
          columns={columns}
          data={data?.invoices}
          empty={{ subtitle: t`settings:billing.invoices.empty-table-label` }}
          enableSorting={false}
          error={error}
          loading={!called || loading}
          onFetchData={handleFetchData}
          onRowClick={onRowClick}
          pageSize={pageSize}
          pagination={enablePagination ? 'links' : 'default'}
          retry={refetch}
          manualFiltering
        />
      </Body>
    </TableCard>
  )
}
